import {
  type FieldMetadata,
  type SubmissionResult,
  getFormProps,
  getInputProps,
  useForm,
  useInputControl,
} from "@conform-to/react";
import { CloseButton } from "@headlessui/react";
import { Trans } from "@lingui/macro";
import { t } from "@lingui/macro";
import { Link } from "@winamp/icons/Link.js";
import {
  Avatar,
  calculateAvatarColor,
} from "@winamp/ui/components/Avatar/Avatar.js";
import { Button } from "@winamp/ui/components/Button/Button.js";
import { Combobox } from "@winamp/ui/components/Combobox/Combobox.js";
import { ComboboxAction } from "@winamp/ui/components/Combobox/ComboboxAction.js";
import { ComboboxDivider } from "@winamp/ui/components/Combobox/ComboboxDivider.js";
import { ComboboxLabel } from "@winamp/ui/components/Combobox/ComboboxLabel.js";
import { ComboboxOption } from "@winamp/ui/components/Combobox/ComboboxOption.js";
import { Dialog } from "@winamp/ui/components/Dialog/Dialog.js";
import { DialogActions } from "@winamp/ui/components/Dialog/DialogActions.js";
import { DialogBody } from "@winamp/ui/components/Dialog/DialogBody.js";
import { DialogDescription } from "@winamp/ui/components/Dialog/DialogDescription.js";
import { DialogTitle } from "@winamp/ui/components/Dialog/DialogTitle.js";
import { ErrorMessages } from "@winamp/ui/components/Fieldset/ErrorMessages.js";
import { Field } from "@winamp/ui/components/Fieldset/Field.js";
import { Label } from "@winamp/ui/components/Fieldset/Label.js";
import { Input } from "@winamp/ui/components/Input/Input.js";
import { classNames } from "@winamp/ui/utils/classNames.js";
import { getValibotConstraint, parseWithValibot } from "conform-to-valibot";
import { type ComponentProps, useEffect, useState } from "react";
import { useFetcher } from "react-router";
import { $path } from "safe-routes";
import * as v from "valibot";
import { useFetcherWithReset } from "~/hooks/use-fetcher-with-reset";
import type { loader as SpotifyArtistLoader } from "~/routes/resources+/streaming-services+/spotify+/artists";
import type { loader as SpotifySearchLoader } from "~/routes/resources+/streaming-services+/spotify+/search";

export type SpotifyImage = {
  url: string;
  width: number;
  height: number;
};

export type SpotifyArtist = {
  id: string;
  name: string;
  images: Array<SpotifyImage>;
  url: string;
};

interface SpotifyComboboxProps {
  spotifyUrlField: FieldMetadata<string>;
  className?: string;
  onChange?: (spotifyArtist: SpotifyArtist | null) => void;
  search?: string | null;
}

function isUrl(value: string | null | undefined) {
  if (!value) return false;
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

export function SpotifyCombobox({
  className,
  search,
  spotifyUrlField,
  onChange,
}: SpotifyComboboxProps) {
  const [enterManualLink, setEnterManualLink] = useState(false);
  const [localSearch, setLocalSearch] = useState<string | null>(null);
  const [selectedArtist, setSelectedArtist] = useState<SpotifyArtist | null>(
    null,
  );
  const fetcher = useFetcher<typeof SpotifySearchLoader>({
    key: "spotify-artists",
  });

  const control = useInputControl(spotifyUrlField);

  const isLoading = fetcher.state !== "idle";

  useEffect(() => {
    const s = localSearch || search;
    const path = isUrl(s)
      ? $path("/resources/streaming-services/spotify/artists", {
          artistUrl: s ?? "",
        })
      : $path("/resources/streaming-services/spotify/search", {
          s: s ?? "",
          type: "artist",
        });
    fetcher.load(path);
  }, [localSearch, search]);

  useEffect(() => {
    if (!spotifyUrlField.value) {
      setSelectedArtist(null);
      setLocalSearch(null);
    }
  }, [spotifyUrlField.value]);

  return (
    <>
      <Field className={className}>
        <Label htmlFor={spotifyUrlField.id}>
          <Trans>Spotify</Trans>
        </Label>
        <Combobox
          setQuery={setLocalSearch}
          displayValue={() => selectedArtist?.name ?? ""}
          displayIcon={() =>
            selectedArtist ? (
              <Avatar
                shape="brand"
                color={calculateAvatarColor(selectedArtist.id)}
                initials={getArtistInitials(selectedArtist.name)}
                imageTransformation={{ transformationParams: { w: 64, h: 64 } }}
                src={selectedArtist.images?.[0]?.url ?? ""}
              />
            ) : null
          }
          onChange={(value) => {
            const selected = fetcher.data?.artists?.find(
              (artist) => artist.id === value,
            );

            if (selected) {
              const artist: SpotifyArtist = {
                id: selected.id ?? "",
                name: selected.name,
                images: selected.images,
                url: selected.external_urls.spotify,
              };
              control.change(artist.url);
              setSelectedArtist(artist);
              onChange?.(artist);
            } else {
              control.change(value);
              setSelectedArtist(null);
              onChange?.(null);
            }

            // Make sure the change event has been processed before blurring the control
            setTimeout(() => {
              control.blur();
            }, 0);
          }}
          onAction={(action) => {
            if (action === "enter-manual-link") {
              setEnterManualLink(true);
            }
          }}
          placeholder={t`Search for an artist profile on Spotify`}
          className={classNames("flex flex-1 [&_input]:pr-7", {
            "[&_input]:pr-14": !!selectedArtist,
          })}
          immediate
          clearable={!!selectedArtist}
          loading={isLoading}
        >
          {fetcher.data?.artists?.map((option) => {
            return (
              <ComboboxOption key={option.id} value={option.id ?? ""}>
                <Avatar
                  shape="brand"
                  color={calculateAvatarColor(option.id)}
                  initials={getArtistInitials(option.name)}
                  imageTransformation={{
                    transformationParams: { w: 64, h: 64 },
                  }}
                  src={option.images[0]?.url}
                />
                <ComboboxLabel>{option.name}</ComboboxLabel>
              </ComboboxOption>
            );
          })}

          {!!fetcher.data?.artists?.length && <ComboboxDivider />}
          <ComboboxAction value="enter-manual-link">
            <Link />
            <ComboboxLabel>
              <Trans>Link profile using a Spotify URL</Trans>
            </ComboboxLabel>
          </ComboboxAction>
        </Combobox>

        <ErrorMessages errors={spotifyUrlField.errors} />
      </Field>
      <SpotifyManualLinkingDialog
        open={enterManualLink}
        onClose={() => setEnterManualLink(false)}
        onLink={(artist) => {
          setSelectedArtist(artist);
          onChange?.(artist);
        }}
      />
    </>
  );
}

function getArtistInitials(name: string) {
  const [first, second] = name.split(" ");
  return second ? `${first[0]}${second[0]}` : `${first[0]}${first[1]}`;
}

interface SpotifyManualLinkingDialogProps
  extends Omit<ComponentProps<typeof Dialog>, "children" | "unmount"> {
  onLink: (artist: SpotifyArtist) => void;
}

const getManualLinkingSchema = () =>
  v.object({
    artistUrl: v.pipe(
      v.string(t`The URL is required`),
      v.url(t`The URL is not valid`),
    ),
  });

function SpotifyManualLinkingDialog({
  onLink,
  ...props
}: SpotifyManualLinkingDialogProps) {
  const [lastResult, setLastResult] = useState<
    SubmissionResult<Array<string>> | null | undefined
  >(null);
  const fetcher = useFetcherWithReset<typeof SpotifyArtistLoader>({
    key: "get-spotify-artist",
  });
  const isPending = fetcher.state !== "idle";

  useEffect(() => {
    setLastResult(fetcher.data);

    if (fetcher.data?.status === "success" && "artists" in fetcher.data) {
      const artist = fetcher.data.artists[0];
      onLink({
        id: artist.id,
        name: artist.name,
        images: artist.images,
        url: artist.external_urls.spotify,
      });
      props.onClose(true);
    }
  }, [fetcher.data]);

  const ManualLinkingSchema = getManualLinkingSchema();
  const [form, fields] = useForm({
    defaultValue: {
      artistUrl: "",
    },
    lastResult,
    constraint: getValibotConstraint(ManualLinkingSchema),
    shouldRevalidate: "onBlur",
    onValidate({ formData }) {
      const validation = parseWithValibot(formData, {
        schema: ManualLinkingSchema,
      });

      return validation;
    },
  });

  return (
    <Dialog {...props} size="lg">
      <DialogTitle>Link profile using a Spotify URL</DialogTitle>
      <DialogDescription className="text-sm/[21px] font-normal text-secondary">
        To copy your Spotify artist link, open the app or website. Search for
        your artist name, click the "..." next to the follow button, select
        "Share," and then choose "Copy link to artist."
      </DialogDescription>

      <DialogBody>
        <fetcher.Form
          method="GET"
          action={$path("/resources/streaming-services/spotify/artists")}
          {...getFormProps(form)}
        >
          <Field className="col-span-full sm:col-span-1">
            <Label htmlFor={fields.artistUrl.id}>
              <Trans>Artist profile</Trans>
            </Label>
            <Input
              {...getInputProps(fields.artistUrl, {
                type: "text",
              })}
              key={fields.artistUrl.key}
              placeholder={t`https://open.spotify.com/artist/...`}
            />
            <ErrorMessages errors={fields.artistUrl.errors} />
            <ErrorMessages errors={form.errors} />
          </Field>
        </fetcher.Form>
      </DialogBody>

      <DialogActions>
        <CloseButton as={Button} variant="ghost" disabled={isPending}>
          <Trans>Cancel</Trans>
        </CloseButton>
        <Button form={form.id} type="submit" autoFocus loading={isPending}>
          <Trans>Confirm</Trans>
        </Button>
      </DialogActions>
    </Dialog>
  );
}
